import { type FC, useEffect, useState } from "react"

import { type Platform } from "@forento/core/models/platform"
import { exhaustiveGuard } from "@forento/core/utilities/switch"
import { cn } from "@forento/ui/utilities/tailwind"

import { usePlatform, useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

import GrantAiCredits from "./GrantAiCredits"
import PlanSelector from "./PlanSelector"

export type Plan = "free" | "appsumo" | "productcanyon" | "basic" | "start" | "scale"

type Corner = "top-left" | "top-right" | "bottom-left" | "bottom-right"

const CORNER_KEY = "admin-panel-corner"

const cornerOrder: Corner[] = ["bottom-left", "bottom-right", "top-right", "top-left"]

const cornerClasses: Record<Corner, string> = {
	"top-left": "top-4 left-4",
	"top-right": "top-4 right-4",
	"bottom-left": "bottom-4 left-4",
	"bottom-right": "bottom-4 right-4",
}

function getPlan(platform: Platform): Plan {
	if (platform.plan.type === "free") return "free"
	if (platform.plan.type === "appsumo") return "appsumo"
	if (platform.plan.type === "productcanyon") return "productcanyon"
	if (platform.plan.id === 3) return "basic"
	if (platform.plan.id === 1) return "start"
	if (platform.plan.id === 2) return "scale"
	return "free"
}

const AdminPanel: FC = () => {
	const userCtx = useUser()
	const platform = usePlatform()

	const [currentPlan, setCurrentPlan] = useState<Plan>(platform ? getPlan(platform) : "free")
	const [corner, setCorner] = useState<Corner>(() => (localStorage.getItem(CORNER_KEY) as Corner) ?? "bottom-left")

	useEffect(() => {
		if (platform) {
			setCurrentPlan(getPlan(platform))
		}
	}, [platform])

	const cycleCorner = () => {
		const next = cornerOrder[(cornerOrder.indexOf(corner) + 1) % cornerOrder.length]
		setCorner(next)
		localStorage.setItem(CORNER_KEY, next)
	}

	const onSuccess = () => userCtx.reload()

	const clearPlan = query.admin.clearPlan.useMutation({ onSuccess })
	const setAppSumo = query.admin.setAppSumo.useMutation({ onSuccess })
	const setProductCanyon = query.admin.setProductCanyon.useMutation({ onSuccess })
	const setPlanId = query.admin.setPlanId.useMutation({ onSuccess })
	const grantAiCredits = query.admin.grantAiCredits.useMutation({ onSuccess })
	const update = query.platform.update.useMutation({ onSuccess })
	const impersonate = query.admin.impersonate.useMutation({ onSuccess })

	if (platform === null) return null

	const handlePlanChange = (plan: Plan) => {
		setCurrentPlan(plan)
		if (plan === "free") {
			clearPlan.mutate()
		} else if (plan === "appsumo") {
			setAppSumo.mutate()
		} else if (plan === "productcanyon") {
			setProductCanyon.mutate()
		} else if (plan === "basic") {
			setPlanId.mutate(3)
		} else if (plan === "start") {
			setPlanId.mutate(1)
		} else if (plan === "scale") {
			setPlanId.mutate(2)
		} else exhaustiveGuard(plan)
	}

	const isImpersonating = userCtx.user?.isImpersonating ?? false

	return (
		<div
			className={cn(
				"fixed rounded-lg border border-white/10 bg-black font-mono text-xs whitespace-nowrap text-white shadow-2xl",
				cornerClasses[corner],
			)}
		>
			<div className="flex items-center gap-1.5 border-b border-white/10 px-3 py-1.5">
				<div className="size-1.5 rounded-full bg-lime-400" />
				<span className="text-xs tracking-widest text-white/40 uppercase">admin</span>
				<button
					type="button"
					onClick={cycleCorner}
					className="ml-auto cursor-pointer text-white/30 transition-colors hover:text-white/70"
					title="Move to next corner"
				>
					⌖
				</button>
			</div>
			<div className="flex items-center gap-px p-1">
				<PlanSelector currentPlan={currentPlan} onPlanChange={handlePlanChange} />
				<GrantAiCredits
					balance={platform.aiCreditBalance}
					isGranting={grantAiCredits.isPending}
					onGrant={amount => grantAiCredits.mutate(amount)}
				/>
				<button
					type="button"
					onClick={() => update.mutate({ themeId: platform.theme.id === "modern" ? "legacy" : "modern" })}
					className="cursor-pointer rounded px-2.5 py-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
				>
					theme: <span className="text-lime-400">{platform.theme.id === "modern" ? "modern" : "legacy"}</span>
				</button>
				<button
					type="button"
					onClick={() =>
						isImpersonating ? userCtx.signOut() : userCtx.user && impersonate.mutate(userCtx.user.id)
					}
					disabled={impersonate.isPending}
					className="cursor-pointer rounded px-2.5 py-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
				>
					impersonate:{" "}
					<span className={isImpersonating ? "text-amber-400" : "text-lime-400"}>
						{isImpersonating ? "on" : "off"}
					</span>
				</button>
			</div>
		</div>
	)
}

export default AdminPanel
